'use client';

import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Paper, Fade } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';
import accountCategories from '../../utils/accountCategory';
import useSaveSpecialItems from '../../hooks/useSaveSpecialItems';

const defaultRows = [
  { account: '141-01-2001', category: 'Line Pipe', item: 'Concrete Coating' },
  { account: '141-01-3003', category: 'Construction Labor', item: 'Deep Water Lay' },
  { account: '141-01-3003', category: 'Construction Labor', item: 'Shallow Water Lay' },
  { account: '141-01-3003', category: 'Construction Labor', item: 'Push/Pull' },
  { account: '141-01-3003', category: 'Construction Labor', item: 'Screw Anchors' },
];

const SpecialItemsGrid = () => {
  const { formData, setFormData } = useEstimator();
  const saveSpecialItems = useSaveSpecialItems();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [specialItems, setSpecialItems] = useState(() => {
    return formData.specialItems?.length > 0
      ? [
          ...defaultRows,
          ...formData.specialItems.filter(
            (item) =>
              !defaultRows.some(
                (row) => row.account === item.account && row.category === item.category && row.item === item.item
              )
          ),
        ]
      : [...defaultRows];
  });

  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Account',
        field: 'account',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: accountCategories.map((item) => item.account),
        },
        editable: (params) => params.data.isNew,
        flex: 1,
      },
      {
        headerName: 'Category',
        field: 'category',
        cellStyle: { backgroundColor: '#f0f0f0' },
        editable: false,
        flex: 1,
      },
      {
        headerName: 'Item',
        field: 'item',
        editable: (params) => params.data.isNew,
        flex: 1,
      },
      {
        headerName: 'Quantity',
        field: 'quantity',
        editable: true,
        valueFormatter: ({ value }) => {
          if (value !== null && !isNaN(Number(value))) {
            return Number(value).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          }
          return '-';
        },
        flex: 1,
      },
      {
        headerName: 'Unit (ft, each, etc.)',
        field: 'unit',
        editable: true,
        flex: 1,
      },
      {
        headerName: 'Unit Cost',
        field: 'unitCost',
        valueFormatter: ({ value }) => {
          if (value !== null && !isNaN(Number(value))) {
            return Number(value).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          }
          return '-';
        },
        editable: true,
        flex: 1,
      },
      {
        headerName: 'Total Cost',
        field: 'totalCost',
        valueGetter: (params) => {
          const qty = parseFloat(params.data.quantity);
          const cost = parseFloat(params.data.unitCost);
          return !isNaN(qty) && !isNaN(cost) ? qty * cost : null;
        },
        valueFormatter: ({ value }) => {
          if (value !== null && !isNaN(Number(value))) {
            return Number(value).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          }
          return '-';
        },
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
        flex: 1,
      },
    ],
    []
  );

  const handleGridEdit = (event) => {
    const updated = [...specialItems];
    const index = event.node.rowIndex;
    const updatedRow = { ...event.data };

    if (updatedRow.account) {
      const match = accountCategories.find((item) => item.account === updatedRow.account);
      updatedRow.category = match ? match.category : '';
    }

    const qty = parseFloat(updatedRow.quantity);
    const cost = parseFloat(updatedRow.unitCost);
    updatedRow.totalCost = !isNaN(qty) && !isNaN(cost) ? (qty * cost).toFixed(2) : '-';

    updated[index] = updatedRow;
    setSpecialItems(updated);
    setFormData((prev) => ({ ...prev, specialItems: updated }));
  };

  const handleAddRow = () => {
    const newRow = {
      account: '',
      category: '',
      item: '',
      quantity: '',
      unit: '',
      unitCost: '',
      isNew: true,
    };
    const updated = [...specialItems, newRow];
    setSpecialItems(updated);
    setFormData((prev) => ({ ...prev, specialItems: updated }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const cleanedItems = specialItems.map((item) => {
        const qty = parseFloat(item.quantity);
        const cost = parseFloat(item.unitCost);
        return {
          ...item,
          totalCost: !isNaN(qty) && !isNaN(cost) ? qty * cost : null,
        };
      });

      const payload = cleanedItems.filter((item) => {
        if (!item.isNew) return true;
        return (
          item.account?.trim() ||
          item.item?.trim() ||
          item.quantity?.toString().trim() ||
          item.unit?.trim() ||
          item.unitCost?.toString().trim()
        );
      });

      await saveSpecialItems({
        estimateId: formData.id || formData.estimateId,
        specialItems: payload,
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save special items:', err);
      alert('Failed to save special items.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in>
      <Box mt={4} display="flex" flexDirection="column" gap={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            backgroundColor: '#8CA3C4',
            color: 'white',
            padding: '8px 16px',
            borderRadius: 1,
          }}
        >
          <Typography variant="h6">Special Items</Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white' }}
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {isCollapsed ? 'Expand' : 'Collapse'}
          </Button>
        </Box>

        {!isCollapsed && (
          <>
            <Paper elevation={3} sx={{ p: 3, backgroundColor: '#F9F9F9', borderRadius: 2 }}>
              <AgGridReact
                rowData={specialItems}
                columnDefs={columnDefs}
                onCellValueChanged={handleGridEdit}
                defaultColDef={{ resizable: true, cellStyle: { textAlign: 'left' } }}
                suppressMovableColumns
                domLayout="autoHeight"
              />
            </Paper>

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button variant="contained" onClick={handleAddRow}>
                Add Row
              </Button>

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading}
                startIcon={loading && <CircularProgress size={18} color="inherit" />}
                sx={{
                  backgroundColor: saved ? '#4CAF50' : '#4A90E2',
                  '&:hover': {
                    backgroundColor: saved ? '#43A047' : '#357ABD',
                  },
                }}
              >
                {loading ? 'Saving...' : saved ? 'Saved!' : 'Save'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Fade>
  );
};

export default SpecialItemsGrid;
